import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ButtonDeleteIds } from 'components/ButtonDeleteIds';
import { SideDetailsLayout } from 'components/Layout/SideDetailsLayout';
import { TableSettings } from 'components/TableSettings';
import type { ThreeForms } from 'components/TotalCount';
import { TotalCount } from 'components/TotalCount';
import type { ChapterEnum, ContractStatusEnum } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { Button, Grid, GridItem } from 'uikit';
import { getCheckedIdsArr } from 'utils';

import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';

import { getUerRowClass } from '../../helpersUerProject';
import { AddCoefficient } from '../AddCoefficient';
import { AddCoefficientToActKC2 } from '../AddCoefficientToActKC2';
import { EditRationaleDialog } from '../EditRationaleDialog';
import { useDeleteWorkOrderPrice } from '../hooks/useDeleteWorkOrderPrice';
import { useSeasons } from '../hooks/useSeasons';
import { WorkOrderSummary } from '../WorkOrderSummary';

import { useActKC2Columns, useSeasonsColumns, useWorkOrderColumns, useWorkOrderDataGetter } from './hooks';
import { useTypeWorkColumns } from './hooks/useTypeWorkColumns';

import './WorkOrderPrice.css';

export const rowsWordForms = ['строка', 'строки', 'строк'] as ThreeForms;
export const TABLE_SETTINGS_HEIGHT = 32;
export const PAGINATION_HEIGHT = 74;

interface IWorkOrderPriceProps {
    workOrderId: number;
    chapter: ChapterEnum;
    workOrderObjectId: number;
    workOrderSubObjectId: number;
    onRefresh: () => void;
    contractStatus?: ContractStatusEnum;
    workOrderStatus?: WorkOrderStatusEnum;
    isAllActKC2Accepted?: boolean;
    reload?: any;
    isDisabledByProjectStatus: boolean;
    workOrderHasInProgress?: boolean;
    costRounding: number;
    contractId: number;
}

const getRowId = (params: any) => params?.data?.id;

export const WorkOrderPrice = forwardRef(
    (
        {
            reload,
            workOrderId,
            chapter,
            workOrderObjectId,
            workOrderSubObjectId,
            workOrderStatus,
            contractStatus,
            isAllActKC2Accepted,
            onRefresh,
            isDisabledByProjectStatus,
            workOrderHasInProgress,
            costRounding,
            contractId,
        }: IWorkOrderPriceProps,
        ref: any,
    ) => {
        const navigate = useNavigate();
        const [seasons] = useSeasons(workOrderId, true);
        const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});

        const tableKey = useMemo(
            () => `${chapter}_${workOrderObjectId}-${workOrderSubObjectId}`,
            [chapter, workOrderObjectId, workOrderSubObjectId],
        );

        const {
            workOrderPriceData,
            workOrderPriceListActs,
            variables,
            refreshPriceListData,
            changeFilterHandler,
            fetchedItems,
        } = useWorkOrderDataGetter({
            gridApiRef: ref.current,
            workOrderObjectId,
            workOrderSubObjectId,
            workOrderId,
            chapter,
            reload,
        });

        useEffect(() => {
            setCheckedIds({});
        }, [variables]);

        useEffect(() => {
            if (workOrderStatus !== WorkOrderStatusEnum.OnFormation) {
                setCheckedIds({});
            }
        }, [workOrderStatus]);

        const [isSummaryDetailsVisible, setIsSummaryDetailsVisible] = useState(false);

        const onSummaryDetailsVisibilityChange = useCallback((isVisible) => {
            setIsSummaryDetailsVisible(isVisible);
        }, []);

        const refreshActs = useCallback(() => {
            onRefresh?.();
        }, [onRefresh]);

        const actKC2Columns = useActKC2Columns({
            actsKC2Data: workOrderPriceListActs,
            handleOnRowChange: refreshPriceListData,
            workOrderStatus,
            contractStatus,
            onRefresh: refreshActs,
            isDisabledByProjectStatus,
            chapter,
            workOrderSubObjectId,
            seasons,
            costRounding,
        });

        const seasonAndLinkedActsColumns = useSeasonsColumns({
            workOrderStatus,
            onChange: refreshPriceListData,
            actsColumns: actKC2Columns,
            contractStatus,
            isDisabledByProjectStatus,
            hasInProgress: workOrderHasInProgress,
            seasons,
            costRounding,
        });

        const hasActs = !!workOrderPriceListActs?.length;

        const checkedIdsArr = useMemo(() => getCheckedIdsArr(checkedIds), [checkedIds]);

        const handlePriceChanges = useCallback(() => {
            if (chapter && workOrderSubObjectId) {
                setCheckedIds({});

                navigate('./', { replace: true, state: { reload: Math.random() } });
            }
        }, [chapter, navigate, workOrderSubObjectId]);

        const handleClearChecked = useCallback(() => {
            setCheckedIds({});
        }, []);

        const {
            deletePriceWorkOrderData,
            deletePriceWorkOrderStatus,
            deletePriceWorkOrderError,
            deletePriceWorkOrderSetData,
            handleDeletePriceWorkOrder,
        } = useDeleteWorkOrderPrice(checkedIdsArr, workOrderStatus);

        const typeWorkColumns = useTypeWorkColumns({
            workOrderStatus,
            workOrderHasInProgress,
            onWorkRatioEditSuccess: refreshPriceListData,
        });

        const { fullSetColumns, handleTableSettingsConfirm, fullSetSettingsColumns, isColumnVisible } =
            useWorkOrderColumns({
                workOrderStatus,
                workOrderId,
                workOrderSubObjectId,
                contractStatus,
                handleSetCheckedIds: setCheckedIds,
                checkedIds,
                isDisabledByProjectStatus,
                hasActs,
                isAllActKC2Accepted,
                chapter,
                seasonAndLinkedActsColumns,
                typeWorkColumns,
                actsData: workOrderPriceListActs,
                changeFilterHandler,
                gridApiRef: ref.current,
                costRounding,
            });

        const tableRowClassGetter = useMemo(() => getUerRowClass(workOrderStatus), [workOrderStatus]);

        return (
            <>
                <Routes>
                    <Route
                        path="/editRationale/:uerId"
                        element={<EditRationaleDialog refreshData={refreshPriceListData} />}
                    />
                    <Route
                        path="/addCoefficient/:uerId"
                        element={
                            workOrderStatus === WorkOrderStatusEnum.InProgress ? (
                                <AddCoefficientToActKC2 actKC2List={workOrderPriceListActs} />
                            ) : (
                                <AddCoefficient workOrderStatus={workOrderStatus} contractId={contractId} />
                            )
                        }
                    />
                    <Route
                        path="/addCoefficient/:uerId/:actKC2Id/:addStrategy"
                        element={<AddCoefficient workOrderStatus={workOrderStatus} contractId={contractId} />}
                    />
                </Routes>
                <Grid cols={2} className="TableSettings">
                    <GridItem>
                        {checkedIdsArr.length ? (
                            <div className="wrapperCount">
                                <TotalCount
                                    totalCount={checkedIdsArr.length}
                                    forms={rowsWordForms}
                                    prefix={checkedIdsArr.length > 1 ? 'Выбрано' : 'Выбрана'}
                                    className="totalCountStyle"
                                />
                                <ButtonDeleteIds
                                    deleteData={deletePriceWorkOrderData}
                                    deleteStatus={deletePriceWorkOrderStatus}
                                    deleteError={deletePriceWorkOrderError}
                                    deleteSetData={deletePriceWorkOrderSetData}
                                    handleDelete={handleDeletePriceWorkOrder}
                                    onSuccess={handlePriceChanges}
                                    style={{ marginRight: '12px' }}
                                />
                                <Button label="Сбросить" view="ghost" size="xs" onClick={handleClearChecked} />
                            </div>
                        ) : (
                            <TotalCount totalCount={fetchedItems} />
                        )}
                    </GridItem>
                    <GridItem style={{ display: 'flex', justifyContent: 'right' }}>
                        <TableSettings
                            columns={fullSetSettingsColumns}
                            onConfirm={handleTableSettingsConfirm}
                            isColumnVisible={isColumnVisible}
                            showPinColumn
                            tableApi={ref?.current}
                        />
                    </GridItem>
                </Grid>
                <SideDetailsLayout
                    onDetailsVisibilityChange={onSummaryDetailsVisibilityChange}
                    collapsedTitle="Показать итоги"
                    expandedTitle="Скрыть итоги"
                >
                    <div className="ag-theme-alpine workOrderPrice">
                        <AgGridTable
                            ref={ref}
                            datasource={workOrderPriceData}
                            columnDefs={fullSetColumns}
                            rowModelType="infinite"
                            maxConcurrentDatasourceRequests={1}
                            maxBlocksInCache={50}
                            cacheBlockSize={50}
                            rowClassRules={tableRowClassGetter}
                            context={{
                                tableUniqueId: `${chapter}_${workOrderObjectId}_${workOrderSubObjectId}`,
                            }}
                            getRowId={getRowId}
                            key={tableKey}
                        />
                    </div>
                    <WorkOrderSummary
                        chapter={chapter}
                        workOrderObjectId={workOrderObjectId}
                        workOrderPriceListData={workOrderPriceListActs}
                        isVisible={isSummaryDetailsVisible}
                        costRounding={costRounding}
                    />
                </SideDetailsLayout>
            </>
        );
    },
);

WorkOrderPrice.displayName = 'WorkOrderPrice';
